import { Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { packages, type Package } from "../../api/packages.service";
import { toApiPackageRequest, type PackageFormData } from "../../schemas/packageSchema";
import { PackageForm } from "../PackageForm";
import styles from "./PackageCreateModal.module.css";

export type PackageCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (pkg: Package) => void;
  onError?: (error: Error) => void;
};

export function PackageCreateModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: PackageCreateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setServerError(null);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  async function handleSubmit(formData: PackageFormData) {
    setIsSubmitting(true);
    setServerError(null);

    try {
      // Convert form data to API request format (only send supported fields)
      const apiRequest = toApiPackageRequest(formData);

      // Create package via API
      const createdPackage = await packages.create(apiRequest);

      // Success
      setIsSubmitting(false);
      onSuccess(createdPackage);
    } catch (err) {
      setIsSubmitting(false);
      const error = err instanceof Error ? err : new Error("Failed to create package");
      setServerError(error.message);

      if (onError) {
        onError(error);
      }
    }
  }

  function handleCancel() {
    if (!isSubmitting) {
      onClose();
    }
  }

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-package-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="create-package-title" className={styles.modalTitle}>
            Create Package
          </h2>
          <button
            className={styles.modalCloseButton}
            onClick={handleCancel}
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {serverError && (
            <div className={styles.serverError} role="alert" aria-live="assertive">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.errorIcon}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              <span>{serverError}</span>
            </div>
          )}

          <PackageForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Create Package"
            isSubmitting={isSubmitting}
            featurePickerColumns={{ mobile: 1, tablet: 3, desktop: 2 }}
            featurePickerCompact={true}
          />
        </div>
      </div>
    </div>
  );
}
